import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ListTreeViewToggle } from "../../../common/components/ListTreeViewToggle/ListTreeViewToggle";
import { KpiSelectorModal } from "../../../common/components/KpiSelectorModal/KpiSelectorModal";
import { LosFilterButton } from "./LosFilterButton/LosFilterButton";
import { SearchSelect } from "../../../common/components/SearchSelect/SearchSelect";
import type { SearchSelectItem } from "../../../common/components/SearchSelect/SearchSelect";
import { IconDownloadButton } from "../../../common/icons/IconDownloadButton/IconDownloadButton";
import { IconRotatingCircle } from "../../../common/icons/IconRotatingCircle/IconRotatingCircle";
import { useAccBootStore } from "../../../common/hooks/useAccBootStore";
import type { LosSearchRecord } from "../../../common/hooks/useAccBootStore";
import type { LosFilterState, LosUiHeaderFilterFlags } from "./LosFilterModal/LosFilterModal";
import { resolveInternationalFilterFlags } from "./LosFilterModal/LosFilterModal";
import { LOS_CUSTOMIZE_EVENT } from "../../Lit/acc-wc-views/src/acc-wc-view-los-data-selector/enum.js";
import { LOS_GRID_EVENT } from "../../Lit/acc-wc-views/src/acc-wc-view-los-indented/enum.js";
import { ExportInProgressEvent } from "../../ServerSideExport/ServerSideExportCommon/ServerSideExportUtil/ServerSideExportEvents";
import { useGetExportListV2Query } from "../../../services/ExportV2/exportV2API";
import { STATUS_DONE, STATUS_IN_PROGRESS, STATUS_PENDING } from "../../../common/constants/export-constants";
import { isExportDisabledBySessionSsoOrigin } from "../../../common/util/exportUtil";
import defaultContent from "../../../content/los";
import "./LosToolbar.scss";
import { getCountryNameFromCode } from "../../../common/util/formatCountryName";
import { storedLocale } from "../../Authentication/util";
import { resolveDisplayName } from "../../../common/util/displayName";
import defaultCommonContent from "../../../content/common";
import maskAboNumber from "../../../common/util/maskAboNumber";
import { useAppSelector } from "../../../store";

// rwlosSearchData is only the flat top-level/isolated set - downlines nested
// under inode.children (already fetched but collapsed) are invisible to a
// shallow scan, so searching for an ABO 2+ levels deep would silently find
// nothing even though __findAndSelectItem can expand straight to it via
// parentMap once given the record. Dedupe by affAbo since a node's subtree can
// appear both as its own top-level entry and nested under another parent.
export function flattenLosSearchRecords(nodes: LosSearchRecord[] | null): LosSearchRecord[] {
  const seen = new Set<string>();
  const flatten = (list: LosSearchRecord[] | null): LosSearchRecord[] =>
    (list ?? []).flatMap((node) => {
      const nested = node.inode?.children?.length ? flatten(node.inode.children) : [];
      if (seen.has(node.affAbo)) return nested;
      seen.add(node.affAbo);
      return [node, ...nested];
    });
  return flatten(nodes);
}

// ─── Types ────────────────────────────────────────────────────────────────────

export interface LosUiHeader extends LosUiHeaderFilterFlags {
  uiShowTreeView?: boolean;
  uiShowExport?: boolean;
  uiShowKpiConfig?: boolean;
  uiShowSearch?: boolean;
}

export interface LosToolbarProps {
  /** From configuration.rwlos.uiHeader */
  uiHeader: LosUiHeader;
  /** From configuration.rwlos.uiKpis */
  availableKpis: string[];
  /** losviewtype passed from RwLos */
  losViewType: string;
  /** Whether the Lit component has finished importing */
  isImported: boolean;
  /** Called when the user clears the LOS search box - resets every piece of state
   *  (in RwLos, TreeView, and the list grid) that remembers the last search result. */
  clearSearchSelection: () => void;
}

// ─── Component ────────────────────────────────────────────────────────────────

// Default KPI selection — mirrors the Lit component's defaultSavedDataViewKpis
// filtered by whatever availableKpis the market config provides.
// Capped at MAX_LOS_KPIS to stay within the KpiSelectorModal's maxSelectionsAllowed.
const DEFAULT_LOS_KPIS = [
  "groupPV",
  "personalPV",
  "bonusPercent",
  "groupBV",
  "vcsPV",
  "nonVCSPV",
  "totalCustomerCount",
  "pointsToNextLevel",
];
const MAX_LOS_KPIS = 8;
const GRID_RETRY_MAX_ATTEMPTS = 10;
const GRID_RETRY_DELAY_MS = 300;

export const LosToolbar = ({
  uiHeader,
  availableKpis,
  losViewType,
  isImported,
  clearSearchSelection,
}: LosToolbarProps) => {
  const { t } = useTranslation(["los", "common"], { useSuspense: false });
  const { state, setStoreState } = useAccBootStore();

  // Track export in-progress state to show spinning circle on the export button
  const [isExportInProgress, setIsExportInProgress] = useState(false);

  // Track export done state for the red notification dot
  const { los: isExportDisabled } = isExportDisabledBySessionSsoOrigin();
  const { data: exportListData } = useGetExportListV2Query({ moduleId: "los" }, { skip: isExportDisabled });
  const exportList = exportListData?.Exports ?? [];
  const showDone = exportList.some(({ ExportStatus }) => ExportStatus === STATUS_DONE);
  const showInProgress = exportList.some(
    ({ ExportStatus }) => ExportStatus === STATUS_PENDING || ExportStatus === STATUS_IN_PROGRESS,
  );

  const isMaskAboNumber = useAppSelector((s) => s.boot.configuration.los.maskConfidentialAboNumber) ?? false;

  useEffect(() => {
    const handleExportInProgress = (e: CustomEvent<{ isInProgress: boolean }>) => {
      setIsExportInProgress(e.detail?.isInProgress ?? false);
    };
    document.addEventListener(ExportInProgressEvent, handleExportInProgress as EventListener);
    return () => document.removeEventListener(ExportInProgressEvent, handleExportInProgress as EventListener);
  }, []);

  // On mount (guarded by isImported), seed acc-boot defaults
  // Default = "View International Frontlines Only" ON:
  //   inInternational=false, hideInternationalDownlines=true, hideVolume=false, hideABOsZeroVolume=false
  useEffect(() => {
    if (isImported) {
      setStoreState({
        // Default: In-Market Frontlines ON, International Frontlines OFF, Downlines OFF
        inInternational: false,
        hideInternationalDownlines: true, // showInternationalDownlines=false by default
        hideVolume: false, // showData=true → hideVolume=false
        hideABOsZeroVolume: false, // showABOsWithZeroVolume=true
        inTreeView: false,
        expandedAbos: [],
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isImported]);

  // ── Derived state ────────────────────────────────────────────────────────────

  const intlVisibility = resolveInternationalFilterFlags(uiHeader);
  const showFilterButton =
    intlVisibility.showIntlFrontlines ||
    intlVisibility.showInMarketFrontlines ||
    intlVisibility.showIntlDownlines ||
    !!uiHeader.uiEnableFilterVolume ||
    !!uiHeader.uiEnableFilterZeroVolume;

  // Map acc-boot store keys → new LosFilterState shape
  // store.inInternational=true means international mode (Show International Frontlines Only)
  // store.inInternational=false means in-market mode (Show In-Market Frontlines Only)
  // hideInternationalDownlines controls downlines visibility in both modes
  const filterState: LosFilterState = {
    showInternationalFrontlines: state.inInternational,
    showInMarketFrontlines: !state.inInternational,
    showInternationalDownlines: !state.hideInternationalDownlines,
    showData: !state.hideVolume,
    showABOsWithZeroVolume: !state.hideABOsZeroVolume,
  };

  // ── Handlers ─────────────────────────────────────────────────────────────────

  const handleViewToggle = () => {
    const nextTreeView = !state.inTreeView;
    setStoreState({ inTreeView: nextTreeView });
    document.dispatchEvent(
      new CustomEvent("acc-es-router-go", {
        bubbles: true,
        composed: true,
        detail: {
          pathname: nextTreeView ? "/$__los-ar/accLosTree" : window.location.pathname,
        },
      }),
    );
  };

  const findIndentedGrid = (): (HTMLElement & Record<string, unknown>) | null => {
    const losArEl = document.getElementById("acc_wc_los_ar_component");
    return (
      (losArEl?.shadowRoot?.querySelector("#component")?.querySelector("acc-wc-view-los-indented") as
        | (HTMLElement & Record<string, unknown>)
        | null) ?? null
    );
  };

  const handleKpisApplied = (selectedKpis: string[], sortedAvailableKpis: string[]) => {
    // Use the same SavedDataView shape returned by savedKpiPreferencesAPI so
    // React and Lit consumers share one event contract.
    const selectedSet = new Set(selectedKpis);
    const kpiItems = sortedAvailableKpis.map((kpi, index) => ({
      id: kpi,
      displaydefault: selectedSet.has(kpi),
      sequence: index,
    }));

    const savedDataView = {
      viewDisplayName: "RW LOS Default View",
      availableKpis: kpiItems,
      timestamp: Date.now(),
    };

    document.dispatchEvent(
      new CustomEvent(LOS_CUSTOMIZE_EVENT.DATA_UPDATED, {
        composed: true,
        bubbles: true,
        detail: { savedDataView },
      }),
    );

    // As a direct fallback, also update the Lit element's reactive property
    // and call __repaintGrid imperatively. This ensures the grid refreshes
    // even if the event listener hasn't been set up yet.
    //
    // The grid (acc-wc-view-los-indented) is imported and mounted lazily by the
    // Lit router, so it may not exist in the shadow DOM yet at the moment the
    // user applies a KPI change (e.g. right after page load). Retry briefly
    // instead of silently giving up on the first miss.
    const applyToGrid = (attempt = 0) => {
      try {
        const indentedGrid = findIndentedGrid();
        if (indentedGrid) {
          indentedGrid.__userSavedDataViewKpis = kpiItems.filter((k) => k.displaydefault);
          const repaint = indentedGrid.__repaintGrid;
          if (typeof repaint === "function") {
            repaint.call(indentedGrid, "lit-virtualizer");
          }
          return;
        }
      } catch {
        // Shadow DOM access may fail in some environments; the event above is the primary path
      }
      if (attempt < GRID_RETRY_MAX_ATTEMPTS) {
        setTimeout(() => applyToGrid(attempt + 1), GRID_RETRY_DELAY_MS);
      }
    };
    applyToGrid();
  };

  const searchCallback = useCallback(
    (text: string): SearchSelectItem[] => {
      const confidentialText = t("common:confidentialUser", defaultCommonContent["confidentialUser"]) as string;
      const notAvailableText = t("common:notAvailable", defaultCommonContent["notAvailable"]) as string;
      const data = flattenLosSearchRecords(state.rwlosSearchData);
      const lower = text.toLowerCase();
      return (
        data
          .filter(
            (r) =>
              r.profile?.name?.toLowerCase().includes(lower) ||
              r.profile?.localName?.toLowerCase().includes(lower) ||
              String(r.abo ?? r.aboNumber ?? "").includes(text),
          )
          // The grid itself refuses to navigate to an international ABO when
          // Show International Downlines is off - matching that here keeps a
          // result from showing up in the dropdown and then silently going
          // nowhere when clicked.
          .filter((r) => !r.flags?.isInternational || !state.hideInternationalDownlines)
          .slice(0, 20)
          .map((r) => {
            const [aff = "", abo = ""] = (r.affAbo || "").split("-");
            const countryLabel = r.profile.country ? getCountryNameFromCode(r.profile.country, storedLocale()) : aff;
            const sublabel = `${isMaskAboNumber && r.flags?.isPrivate ? maskAboNumber(abo) : abo} | ${countryLabel}`;
            const resolvedName = resolveDisplayName({
              name: r.profile?.name,
              localName: r.profile?.localName,
              privacyFlag: r.flags?.isPrivate,
              languageCode: r.profile?.preferredLanguageCode,
              locale: storedLocale(),
              confidentialText,
              notAvailableText,
            });
            const label =
              resolvedName === notAvailableText ? String(r.abo ?? r.aboNumber ?? notAvailableText) : resolvedName;
            return {
              key: r.affAbo,
              label,
              sublabel,
            };
          })
      );
    },
    [state.rwlosSearchData, state.hideInternationalDownlines, t, isMaskAboNumber],
  );

  const handleSearchSelection = (affAbo: string): { displayName: string } => {
    const confidentialText = t("common:confidentialUser", defaultCommonContent["confidentialUser"]) as string;
    const notAvailableText = t("common:notAvailable", defaultCommonContent["notAvailable"]) as string;
    const record = flattenLosSearchRecords(state.rwlosSearchData).find((r) => r.affAbo === affAbo);
    document.dispatchEvent(
      new CustomEvent(LOS_GRID_EVENT.DATA_FIND, {
        bubbles: true,
        composed: true,
        detail: { record },
      }),
    );
    const resolvedName = record
      ? resolveDisplayName({
          name: record.profile?.name,
          localName: record.profile?.localName,
          privacyFlag: record.flags?.isPrivate,
          languageCode: record.profile?.preferredLanguageCode,
          locale: storedLocale(),
          confidentialText,
          notAvailableText,
        })
      : notAvailableText;
    const displayName = resolvedName === notAvailableText ? affAbo : resolvedName;
    return { displayName };
  };

  const handleFiltersApply = (next: LosFilterState) => {
    // Map new LosFilterState → acc-boot store keys.
    // hideInternationalDownlines is now independent of which frontlines mode is active —
    // it reflects the user's explicit "Show International Downlines" toggle choice.
    setStoreState({
      inInternational: next.showInternationalFrontlines,
      hideInternationalDownlines: !next.showInternationalDownlines,
      hideVolume: !next.showData,
      hideABOsZeroVolume: !next.showABOsWithZeroVolume,
    });
  };

  const handleExportClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (isExportDisabledBySessionSsoOrigin().los) return;
    window.dispatchEvent(
      new CustomEvent("OpenExportV2Modal", {
        bubbles: true,
        composed: true,
        detail: {
          moduleId: losViewType,
          open: true,
          target: event.currentTarget,
        },
      }),
    );
  };

  const handleSearchClear = () => {
    // SearchSelect handles its own input text/dropdown clear; delegate to the parent
    // for everything else it owns (RwLos's cross-view memory, TreeView's selection,
    // and the list grid's highlighted row).
    clearSearchSelection();
  };

  // ── Render ───────────────────────────────────────────────────────────────────

  return (
    <div className="LosToolbar">
      <div className="LosToolbar__buttons">
        {uiHeader.uiShowTreeView && (
          <ListTreeViewToggle
            isTreeView={state.inTreeView}
            onToggle={handleViewToggle}
            disabled={state.loading}
            label={t("toolbar.treeViewLabel", defaultContent.toolbar.treeViewLabel)}
          />
        )}
        {uiHeader.uiShowKpiConfig && (
          <div className="LosToolbar__kpiSelector">
            <KpiSelectorModal
              availableKpis={availableKpis}
              defaultKpis={DEFAULT_LOS_KPIS.filter((kpi) => availableKpis.includes(kpi))}
              saveViewName="accWcLosCustomizedFields"
              saveViewDescription="RW LOS Default View"
              maxSelectionsAllowed={MAX_LOS_KPIS}
              disabled={state.loading}
              onApplyTrack={handleKpisApplied}
            />
          </div>
        )}
        {showFilterButton && (
          <LosFilterButton
            uiHeader={uiHeader}
            filterState={filterState}
            inTreeView={state.inTreeView}
            disabled={state.loading}
            onApply={handleFiltersApply}
          />
        )}
      </div>
      {uiHeader.uiShowSearch && (
        <div className="LosToolbar__search">
          <SearchSelect
            placeholderText={t("toolbar.searchPlaceholder", defaultContent.toolbar.searchPlaceholder)}
            clearSearchAriaLabel={t("toolbar.clearSearchAriaLabel", defaultContent.toolbar.clearSearchAriaLabel)}
            searchCallback={searchCallback}
            selectionCallback={handleSearchSelection}
            searchText=""
            disabled={state.loading}
            clear={handleSearchClear}
          />
        </div>
      )}
      {uiHeader.uiShowExport && (
        <button
          className="LosToolbar__exportButton"
          onClick={handleExportClick}
          disabled={state.loading || isExportDisabled}
          aria-label={t("toolbar.exportAriaLabel", defaultContent.toolbar.exportAriaLabel)}
          type="button"
        >
          {showInProgress || isExportInProgress ? (
            <span data-testid="circle-rotating-icon">
              <IconRotatingCircle />
            </span>
          ) : (
            <IconDownloadButton />
          )}
          {showDone && !showInProgress && !isExportInProgress && (
            <span className="LosToolbar__exportButton__notificationDot" data-testid="export-notification-dot" />
          )}
        </button>
      )}
    </div>
  );
};
